/**
 * Central auth state machine for the playground.
 *
 * Owns auth state, emits a single `rundot-game-state-changed` event,
 * and coordinates the sign-in → profile → game-creation → ready flow.
 *
 * PlaygroundHost delegates to this; the toolbar listens to it.
 */

import { getFirebaseClient, type FirebaseClient } from '../firebase/firebaseClient'
import { getPlaygroundConfig } from '../config/playground'
import { setGoogleSignedIn, clearRegistry } from './tabCoordinator'
import { markBootPhase } from './BootTelemetry'

// ─── State Types ────────────────────────────────────────────────────────────

export type PlaygroundAuthState =
  | { phase: 'initializing' }
  | { phase: 'awaiting-sign-in' }
  | { phase: 'creating-profile'; user: AuthUser }
  | { phase: 'creating-game'; user: AuthUser }
  | { phase: 'loading-game'; user: AuthUser }
  | { phase: 'ready'; user: AuthUser }
  | { phase: 'error'; message: string }

export interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  isAnonymous: boolean
  photoURL?: string | null
}

type Subscriber = (state: PlaygroundAuthState) => void

// ─── Callbacks provided by PlaygroundHost ──────────────────────────────────────

export interface StateMachineCallbacks {
  /** Called when a user signs in. Should ensure profile exists and set _profileData. */
  onCreateProfile: (user: import('firebase/auth').User) => Promise<void>
  /** Called after profile is ready. Should fetch backend profile and upgrade _profileData. */
  onFetchProfile: (user: import('firebase/auth').User) => Promise<void>
  /** Called in playground when no gameId — should auto-create game (ends in page reload). */
  onNeedsGameCreation: (user: import('firebase/auth').User) => Promise<void>
  /** Called when user is null on first callback (no cached user). Optional setup. */
  onNoUser?: () => void
}

// ─── State Machine ──────────────────────────────────────────────────────────

export class PlaygroundAuthStateMachine {
  private _state: PlaygroundAuthState = { phase: 'initializing' }
  private _subscribers: Subscriber[] = []
  private _profileResolve: (() => void) | null = null
  private _profileReady: Promise<void>
  private _authUnsubscribe: (() => void) | null = null
  private _changeId = 0
  private _expectedUid: string | null = null
  private _client: FirebaseClient | null = null
  private _gameInitialized = false

  constructor(private callbacks: StateMachineCallbacks) {
    this._profileReady = new Promise<void>((resolve) => {
      this._profileResolve = resolve
    })
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  get state(): PlaygroundAuthState {
    return this._state
  }

  /** Wait for the profile to be ready (called by PlaygroundHost.initialize). */
  waitForProfile(): Promise<void> {
    return this._profileReady
  }

  /**
   * Mark the game as ready (called after PlaygroundHost.initialize completes).
   *
   * If the auth flow hasn't reached `loading-game` yet (e.g. user wasn't signed in
   * when initialize() ran), this sets a flag so the transition to `ready` happens
   * automatically when `loading-game` is eventually reached after sign-in.
   */
  markGameReady(): void {
    this._gameInitialized = true
    if (this._state.phase === 'loading-game') {
      this.transition({ phase: 'ready', user: this._state.user })
    }
  }

  /** Subscribe to state changes. Returns unsubscribe function. */
  subscribe(fn: Subscriber): () => void {
    this._subscribers.push(fn)
    return () => {
      this._subscribers = this._subscribers.filter(s => s !== fn)
    }
  }

  /** Get the Firebase client (available after start()). */
  getClient(): FirebaseClient | null {
    return this._client
  }

  /** Clean up subscriptions. */
  destroy(): void {
    if (this._authUnsubscribe) {
      this._authUnsubscribe()
      this._authUnsubscribe = null
    }
    this._subscribers = []
  }

  // ─── Start the machine ──────────────────────────────────────────────────

  /**
   * Initialize Firebase and set up the single onAuthStateChanged listener.
   * Returns the Firebase client promise (same as getFirebaseClient).
   */
  async start(): Promise<FirebaseClient> {
    this.transition({ phase: 'initializing' })

    try {
      const client = await getFirebaseClient()
      this._client = client

      let isFirstCallback = true
      this._authUnsubscribe = client.onAuthStateChanged(async (user) => {
        const changeId = ++this._changeId
        const expectedUid = user?.uid ?? null
        this._expectedUid = expectedUid
        const isStale = (): boolean =>
          this._changeId !== changeId || this._expectedUid !== expectedUid

        // Capture and clear isFirstCallback immediately to avoid races
        // where a rapid sign-out during async work sees stale isFirstCallback=true.
        const isFirst = isFirstCallback
        isFirstCallback = false

        if (!user) {
          // ── No user ──────────────────────────────────────────────────
          if (isFirst) {
            this.callbacks.onNoUser?.()
            // For playground: don't resolve profileReady yet — the game
            // must wait for sign-in so _profileData is populated before
            // initialize() proceeds. Without this, sign-out → sign-in
            // results in the game loading with an empty profile.
            // For non-playground (dev/staging/local): resolve immediately
            // so initialize() can proceed (user will sign in via toolbar
            // and onAuthStateChanged will update _profileData in-place).
            const config = getPlaygroundConfig()
            if (config?.target !== 'playground') {
              this.resolveProfile()
            }
          } else {
            // Sign-out detected after being signed in → reload for all targets
            this.handleSignOut()
            return
          }
          this.transition({ phase: 'awaiting-sign-in' })
          return
        }

        // ── User present ──────────────────────────────────────────────
        const authUser = toAuthUser(user)

        // Step 1: Create profile
        this.transition({ phase: 'creating-profile', user: authUser })
        await this.callbacks.onCreateProfile(user)
        if (isStale()) return

        // Step 1.5: For playground slot 0, notify other tabs
        const tabInfo = typeof window !== 'undefined' ? window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__ : undefined
        if (getPlaygroundConfig()?.target === 'playground' && tabInfo?.slot === 0 && !user.isAnonymous) {
          setGoogleSignedIn(true)
        }

        // Step 2: Auto-create game for playground when no gameId.
        // onNeedsGameCreation reloads the page on success; on failure it calls setError()
        // which resolves waitForProfile. Recovery requires a page reload.
        const config = getPlaygroundConfig()
        if (
          config?.target === 'playground' &&
          !config.gameId &&
          config.gameManagerUrl
        ) {
          this.transition({ phase: 'creating-game', user: authUser })
          await this.callbacks.onNeedsGameCreation(user)
          return
        }

        if (isStale()) return

        // Step 3: Fetch backend profile
        await this.callbacks.onFetchProfile(user)
        if (isStale()) return

        // Step 4: Profile ready → loading game (or straight to ready if initialize() already ran)
        this.transition({ phase: 'loading-game', user: authUser })
        this.resolveProfile()
        if (this._gameInitialized) {
          this.transition({ phase: 'ready', user: authUser })
        }
      })

      return client
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      this.transition({ phase: 'error', message })
      this.resolveProfile() // Don't hang initialize() forever
      throw error
    }
  }

  /** Transition to error state (can be called externally, e.g. game creation failure). */
  setError(message: string): void {
    this.transition({ phase: 'error', message })
    this.resolveProfile()
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  private transition(newState: PlaygroundAuthState): void {
    this._state = newState
    // Boot telemetry: one mark per state transition (no-op outside playground).
    markBootPhase(`state:${newState.phase}`)
    // Dispatch DOM event for toolbar (runs in a separate JS context)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('rundot-game-state-changed', {
        detail: { ...newState },
      }))
    }
    // Notify in-process subscribers
    for (const fn of this._subscribers) {
      try { fn(newState) } catch (e) { console.error('[RUN] State subscriber error:', e) }
    }
  }

  private resolveProfile(): void {
    if (this._profileResolve) {
      this._profileResolve()
      this._profileResolve = null
    }
  }

  private handleSignOut(): void {
    const config = getPlaygroundConfig()
    if (config?.target === 'playground') {
      clearRegistry()
    }
    if (config?.target === 'local') {
      try {
        localStorage.removeItem('rundot-game-sandbox-selected-player')
        sessionStorage.setItem('rundot-game-sandbox-signed-out', '1')
      } catch { /* storage unavailable */ }
    }
    window.location.reload()
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function toAuthUser(user: import('firebase/auth').User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    isAnonymous: user.isAnonymous,
    photoURL: user.photoURL,
  }
}
